import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { formatCPFOrCNPJ } from 'src/app/helpers/utils/cpf-cnpj.util';
import { PsaAuthorizationService } from 'src/app/services/auth/psa-authorization.service';
import {
  BiomesRercord,
  Developer,
  Locations,
  ProjectStatus,
  ProjectStatusCode,
  ProjectUpdateStatus,
  PsaProjectData,
  getProjectStatusDisplayLabel,
  getProjectStatusIcon,
} from 'src/app/shared/interfaces/psa-project-register';
import { ProjectBaselineActivityService } from '../../../services/project-baseline-activity.service';
import { ProjectMethodologiesService } from '../../../services/project-methodology.service';
import { ProjectService } from '../../../services/project.service';

export interface PropertyLocationView {
  ownerName: string;
  ownerDocument: string;
  municipality: string;
  state: string;
  coordinates: string;
  totalHectares: string;
  carbonHectares: string;
  biome: string;
  car: string;
}

@Component({
  standalone: false,
  selector: 'app-project-validation-details-drawer',
  templateUrl: './project-validation-details-drawer.component.html',
  styleUrls: ['./project-validation-details-drawer.component.scss'],
})
export class ProjectValidationDetailsDrawerComponent implements OnChanges, OnDestroy {
  @Input() open = false;
  @Input() projectId: number | null = null;
  @Output() openChange = new EventEmitter<boolean>();
  @Output() projectUpdated = new EventEmitter<void>();
  @Output() projectApproved = new EventEmitter<{ projectId: number; projectName: string }>();
  @Output() projectRejected = new EventEmitter<{ projectId: number; projectName: string }>();

  projectData?: PsaProjectData;
  developerPrincipal?: Developer;
  otherDevelopers: Developer[] = [];
  propertyLocations: PropertyLocationView[] = [];
  baselineActivity = '-';
  methodologyLabel = '-';
  isLoading = false;

  showRejectStartModal = false;
  showRejectConfirmModal = false;
  showApproveModal = false;
  pendingRejectionComment = '';

  private hasAuditPermission = false;
  private loadSubscription?: Subscription;
  private updateSubscription?: Subscription;

  constructor(
    private readonly projectService: ProjectService,
    private readonly projectBaselineActivityService: ProjectBaselineActivityService,
    private readonly projectMethodologiesService: ProjectMethodologiesService,
    private readonly psaAuthorizationService: PsaAuthorizationService,
    private readonly datePipe: DatePipe,
  ) {}

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.updateSubscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['open'] || changes['projectId']) && this.open && this.projectId != null) {
      this.loadPermissions();
      this.loadProjectDetails(this.projectId);
    }

    if (changes['open'] && !this.open) {
      this.resetState();
    }
  }

  get statusTone(): 'info' | 'success' | 'danger' | 'warning' | 'default' {
    return this.resolveStatusTone(this.projectData?.status);
  }

  get statusLabel(): string {
    return getProjectStatusDisplayLabel(this.projectData?.status);
  }

  get statusIcon(): string {
    return getProjectStatusIcon(this.projectData?.status);
  }

  get showAuditActions(): boolean {
    return this.hasAuditPermission
      && this.projectData?.status === ProjectStatus.PROJECT_PENDING_AUDITOR_VALIDATION;
  }

  get showRejectionReason(): boolean {
    return this.projectData?.status === ProjectStatus.PROJECT_REJECTED_BY_AUDITOR;
  }

  get developerPhoneLabel(): string {
    return this.formatPhone(this.developerPrincipal?.phone);
  }

  get documentLinks(): string[] {
    return (this.projectData?.attachments ?? [])
      .map(attachment => attachment.link)
      .filter((link): link is string => !!link?.trim());
  }

  get selectedOdsTags(): string[] {
    return (this.projectData?.odsNumbers ?? [])
      .filter(odsNumber => Number.isInteger(odsNumber))
      .map(odsNumber => `ODS ${odsNumber}`);
  }

  get selectedOdsCountLabel(): string {
    const count = this.selectedOdsTags.length;

    if (!count) {
      return 'ODS selecionados';
    }

    return count === 1 ? '1 ODS selecionado' : `${count} ODS selecionados`;
  }

  close(): void {
    this.open = false;
    this.openChange.emit(false);
  }

  onDrawerClose(): void {
    this.close();
  }

  onDrawerBack(): void {
    this.close();
  }

  openRejectModal(): void {
    this.showRejectStartModal = true;
  }

  onRejectStartModalClosed(): void {
    this.showRejectStartModal = false;
  }

  onRejectFormConfirmed(comment: string): void {
    this.showRejectStartModal = false;
    this.pendingRejectionComment = comment;
    this.showRejectConfirmModal = true;
  }

  onRejectConfirmModalClosed(confirmed: boolean): void {
    this.showRejectConfirmModal = false;

    if (!confirmed) {
      this.pendingRejectionComment = '';
      return;
    }

    this.submitRejection();
  }

  openApproveConfirmation(): void {
    this.showApproveModal = true;
  }

  onApproveModalClosed(confirmed: boolean): void {
    this.showApproveModal = false;

    if (!confirmed) {
      return;
    }

    this.updateProjectStatus(
      ProjectStatusCode.PROJECT_PENDING_DEVELOPER_CONFIRMATION,
      '',
      true,
      'approve',
    );
  }

  private submitRejection(): void {
    const description = this.pendingRejectionComment.trim();

    if (!description) {
      return;
    }

    this.updateProjectStatus(
      ProjectStatusCode.PROJECT_REJECTED_BY_AUDITOR,
      description,
      false,
      'reject',
    );
  }

  private loadPermissions(): void {
    const psaFunctions = this.psaAuthorizationService.getFunctions();
    this.hasAuditPermission = psaFunctions.some(
      (f: { functionCode?: string }) => f.functionCode === 'CAR_PSA_AUDITA_PROJETO',
    );
  }

  private loadProjectDetails(projectId: number): void {
    this.loadSubscription?.unsubscribe();
    this.isLoading = true;

    this.loadSubscription = this.projectService.getProjectById(projectId).subscribe({
      next: response => {
        const project = response?.data;
        if (!project) {
          this.isLoading = false;
          return;
        }

        this.projectData = this.projectService.parseProjectStatus(project);
        this.projectData.projectStartDate = this.formatDate(this.projectData.projectStartDate);
        this.projectData.projectEndDate = this.formatDate(this.projectData.projectEndDate);

        this.developerPrincipal = this.projectData.developers?.find(developer => developer.principal);
        this.otherDevelopers = (this.projectData.developers ?? [])
          .filter(developer => !developer.principal)
          .map(developer => ({
            ...developer,
            cpfCnpj: developer.cpfCnpj
              ? formatCPFOrCNPJ(developer.cpfCnpj) ?? developer.cpfCnpj
              : developer.cpfCnpj,
          }));

        if (this.developerPrincipal?.cpfCnpj) {
          this.developerPrincipal.cpfCnpj = formatCPFOrCNPJ(this.developerPrincipal.cpfCnpj)
            ?? this.developerPrincipal.cpfCnpj;
        }

        this.propertyLocations = this.mapPropertyLocations(this.projectData.locations ?? []);
        this.loadBaselineActivity(this.projectData.baselineActivityId);
        this.loadMethodology(this.projectData.methodologyId);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  private updateProjectStatus(
    status: ProjectStatusCode,
    description: string,
    clearDescription: boolean,
    action: 'approve' | 'reject',
  ): void {
    if (this.projectId == null) {
      console.error('Id do projeto inválido, não é possível atualizar o status.');
      return;
    }

    const data: ProjectUpdateStatus = {
      status,
      description: clearDescription ? '' : description,
    };

    const projectId = this.projectId;
    const projectName = this.projectData?.projectName?.trim() ?? '';

    this.updateSubscription?.unsubscribe();
    this.updateSubscription = this.projectService.updateProjectStatus(projectId, data).subscribe({
      next: () => {
        this.pendingRejectionComment = '';
        this.projectUpdated.emit();

        if (action === 'approve') {
          this.projectApproved.emit({ projectId, projectName });
        }

        if (action === 'reject') {
          this.projectRejected.emit({ projectId, projectName });
        }

        this.close();
      },
      error: error => {
        console.error('Erro ao atualizar o status do projeto.', error);
      },
    });
  }

  private loadBaselineActivity(baselineActivityId?: number | null): void {
    if (baselineActivityId == null) {
      this.baselineActivity = '-';
      return;
    }

    this.projectBaselineActivityService.getBaselinesActivity().subscribe(response => {
      const baselines = response?.data ?? [];
      const match = baselines.find(
        (item: { baselineActivityIdentifier: number }) => item.baselineActivityIdentifier === baselineActivityId,
      );
      this.baselineActivity = match?.baselineActivityDescription ?? '-';
    });
  }

  private loadMethodology(methodologyId?: number | null): void {
    if (methodologyId == null) {
      this.methodologyLabel = '-';
      return;
    }

    this.projectMethodologiesService.getMethodologies().subscribe(response => {
      const methodologies = response?.data ?? [];
      const match = methodologies.find(
        (item: { methodologyIdentifier: number }) => item.methodologyIdentifier === methodologyId,
      );
      this.methodologyLabel = match?.methodologyDescription ?? '-';
    });
  }

  private resetState(): void {
    this.loadSubscription?.unsubscribe();
    this.updateSubscription?.unsubscribe();
    this.projectData = undefined;
    this.developerPrincipal = undefined;
    this.otherDevelopers = [];
    this.propertyLocations = [];
    this.baselineActivity = '-';
    this.methodologyLabel = '-';
    this.isLoading = false;
    this.showRejectStartModal = false;
    this.showRejectConfirmModal = false;
    this.showApproveModal = false;
    this.pendingRejectionComment = '';
  }

  private formatPhone(phone?: string | null): string {
    if (!phone?.trim()) {
      return '-';
    }

    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) {
      return phone.trim();
    }

    return `(${digits.slice(0, 2)})${digits.slice(2)}`;
  }

  private formatDate(value?: string | null): string {
    if (!value) {
      return '-';
    }

    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) {
      return value;
    }

    return this.datePipe.transform(parsed, 'dd/MM/yyyy') ?? value;
  }

  private mapPropertyLocations(locations: Locations[]): PropertyLocationView[] {
    if (!locations.length) {
      return [this.createEmptyPropertyLocation()];
    }

    return locations.map(location => ({
      ownerName: location.landOwnerFullName || '-',
      ownerDocument: location.landOwnerCpfCnpj
        ? formatCPFOrCNPJ(location.landOwnerCpfCnpj) ?? location.landOwnerCpfCnpj
        : '-',
      municipality: location.municipality || '-',
      state: location.state || '-',
      coordinates: this.formatCoordinates(location.latitude, location.longitude),
      totalHectares: this.formatHectares(location.totalHectares),
      carbonHectares: this.formatHectares(location.carbonGenerationHectares),
      biome: this.formatBiome(location.biome),
      car: location.registrationRecord || '-',
    }));
  }

  private createEmptyPropertyLocation(): PropertyLocationView {
    return {
      ownerName: '-',
      ownerDocument: '-',
      municipality: '-',
      state: '-',
      coordinates: '-',
      totalHectares: '-',
      carbonHectares: '-',
      biome: '-',
      car: '-',
    };
  }

  private formatCoordinates(latitude?: string, longitude?: string): string {
    if (!latitude && !longitude) {
      return '-';
    }

    const lat = this.formatCoordinatePart(latitude);
    const long = this.formatCoordinatePart(longitude);

    if (lat === '-' && long === '-') {
      return '-';
    }

    return `Lat ${lat} / Long ${long}`;
  }

  private formatCoordinatePart(value?: string): string {
    if (!value?.trim()) {
      return '-';
    }

    const trimmed = value.trim();
    const directionMatch = trimmed.match(/\s*([A-Za-z])$/);
    const direction = directionMatch ? directionMatch[1].toUpperCase() : '';
    const numericPart = directionMatch
      ? trimmed.slice(0, directionMatch.index).trim()
      : trimmed;
    const isNegative = numericPart.startsWith('-');
    const digits = numericPart.replace(/\D/g, '');

    if (!digits) {
      return trimmed;
    }

    const grouped = digits.match(/.{1,2}/g)?.join('.') ?? digits;
    const formattedValue = isNegative ? `-${grouped}` : grouped;

    return direction ? `${formattedValue} ${direction}` : formattedValue;
  }

  private formatHectares(value?: number | null): string {
    if (value == null) {
      return '-';
    }

    return `${value.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ha`;
  }

  private formatBiome(biome?: string): string {
    if (!biome) {
      return '-';
    }

    const label = BiomesRercord[biome] ?? biome.replace(/_/g, ' ');

    return label.charAt(0) + label.slice(1).toLowerCase();
  }

  private resolveStatusTone(status?: string): 'info' | 'success' | 'danger' | 'warning' | 'default' {
    switch (status) {
      case ProjectStatus.PROJECT_REJECTED_BY_AUDITOR:
      case ProjectStatus.PROJECT_REJECTED_BY_DEVELOPER:
        return 'danger';
      case ProjectStatus.PROJECT_APPROVED_BY_AUDITOR:
      case ProjectStatus.PROJECT_CONFIRMED_BY_DEVELOPER:
      case ProjectStatus.PROJECT_REGISTERED_IN_B3:
        return 'success';
      case ProjectStatus.PROJECT_PENDING_AUDITOR_VALIDATION:
      case ProjectStatus.PROJECT_DRAFT:
        return 'warning';
      default:
        return 'info';
    }
  }
}
